import { IField } from 'components/shared/form/IField';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { getField } from './formHelpers';
import DestinationWarehouseCostType from '../types/DestinationWarehouseCost';

type StatefulPriceField = PriceField & {
  set: React.Dispatch<React.SetStateAction<string>>;
};

type PriceField = {
  field: IField;
  value: string;
};

type CheckboxField = {
  field: IField;
  value: string;
  set: React.Dispatch<React.SetStateAction<string>>;
};

type SelectField = {
  field: IField;
  value: string;
  set: React.Dispatch<React.SetStateAction<string>>;
};

type ValueSet = {
  value: string;
  set: React.Dispatch<React.SetStateAction<string>>;
};

type PriceContext = {
  fixedPriceType?: PriceField;
  opportunityPriceUsdLb?: StatefulPriceField;
  opportunityFixedPrice?: StatefulPriceField;
  modifiedExchangeRate?: CheckboxField;
  contractPricesUsdToCustomerRate?: StatefulPriceField;
  contractPricesCustomerToUsdRate?: StatefulPriceField;
  opportunityPricesUsdToCustomerRate?: StatefulPriceField;
  selectedPackagingTypeCapacity?: ValueSet;
  units?: StatefulPriceField;
  importMarkup?: StatefulPriceField;
  importMarkupUsdLb?: StatefulPriceField;
  otherMarkups?: StatefulPriceField;
  otherMarkupsUsdLb?: StatefulPriceField;
  fobPrice?: StatefulPriceField;
  fobPriceUsdLb?: StatefulPriceField;
  spotIcoCost?: StatefulPriceField;
  fixedPrice?: StatefulPriceField;
  priceUsdLb?: StatefulPriceField;
  exchangeRate?: number;
  measurementRate?: number;
  differentialPrice?: StatefulPriceField;
  opportunityPrice?: StatefulPriceField;
  internalContractPrice?: StatefulPriceField;
  loadoutSelected?: CheckboxField;
  freightSelected?: CheckboxField;
  otherChargesSelected?: CheckboxField;
  carrySelected?: CheckboxField;
  warehousingSelected?: CheckboxField;
  incoterm?: string;
  carryPercentagePerMonth?: SelectField;
  carryMonthsIncluded?: SelectField;
  loadoutCost?: StatefulPriceField;
  freightCost?: StatefulPriceField;
  otherChargesCost?: StatefulPriceField;
  carryCost?: StatefulPriceField;
  warehousingCost?: StatefulPriceField;
  sameCurrency?: boolean;
  sameUnitofMeasure?: boolean;
  sameCurrencyUnitofMeasure?: boolean;
  contractType?: string;
  // PLA-1034/1035: los setea <PriceSimulator/> y los lee <Price/> para pintar en rojo cada
  // campo cuando SU precio ingresado queda por debajo del calculado por el Price Simulator.
  // Cada uno responde a su propia comparación (el FOB no debe pintar el Precio, ni al revés).
  priceBelowSimulator?: boolean;
  setPriceBelowSimulator?: React.Dispatch<React.SetStateAction<boolean>>;
  fobBelowSimulator?: boolean;
  setFobBelowSimulator?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const PriceContext = createContext<PriceContext>({});

export const usePriceContext = () => useContext(PriceContext);

function createPrice(field: IField): PriceField {
  return {
    field,
    value: field.value ?? '0.0',
  };
}

function createStatefulPrice(field: IField): StatefulPriceField {
  const { value } = createPrice(field);

  const [price, setPrice] = useState(value);

  return {
    field,
    value: price,
    set: setPrice,
  };
}

function createCheckboxPrice(field: IField): StatefulPriceField {
  const [checboxOption, setCheckboxOption] = useState(field.value ?? 'false');

  return {
    field,
    value: checboxOption,
    set: setCheckboxOption,
  };
}

function createSelectPrice(field: IField): StatefulPriceField {
  const [selectOption, setSelectOption] = useState(field.value ?? 'false');

  return {
    field,
    value: selectOption,
    set: setSelectOption,
  };
}

function createValueSet(value: string): ValueSet {
  const [price, setPrice] = useState(value);

  return {
    value: price,
    set: setPrice,
  };
}

export const usePrice = (
  fields: IField[],
  destinationWarehouseCosts: DestinationWarehouseCostType[],
  destinationWarehouseId: string | null,
  incoterm?: string,
  exchangeRate?: number,
  measurementRate?: number,
  currency?: string,
  unitOfMeasure?: string,
  contractType?: string
): PriceContext => {
  const [priceBelowSimulator, setPriceBelowSimulator] = useState(false);
  const [fobBelowSimulator, setFobBelowSimulator] = useState(false);
  let selectedPackagingTypeCapacity: undefined | ValueSet;
  let units: undefined | StatefulPriceField;
  let fixedPriceType: undefined | PriceField;
  let importMarkup: undefined | StatefulPriceField;
  let importMarkupUsdLb: undefined | StatefulPriceField;
  let otherMarkups: undefined | StatefulPriceField;
  let otherMarkupsUsdLb: undefined | StatefulPriceField;
  let fobPrice: undefined | StatefulPriceField;
  let fobPriceUsdLb: undefined | StatefulPriceField;
  let spotIcoCost: undefined | StatefulPriceField;
  let fixedPrice: undefined | StatefulPriceField;
  let priceUsdLb: undefined | StatefulPriceField;
  let modifiedExchangeRate: undefined | CheckboxField;
  let contractPricesUsdToCustomerRate: undefined | StatefulPriceField;
  let contractPricesCustomerToUsdRate: undefined | StatefulPriceField;
  let opportunityPriceUsdLb: undefined | StatefulPriceField;
  let opportunityFixedPrice: undefined | StatefulPriceField;
  let opportunityPricesUsdToCustomerRate: undefined | StatefulPriceField;
  let differentialPrice: undefined | StatefulPriceField;
  let opportunityPrice: undefined | StatefulPriceField;
  let internalContractPrice: undefined | StatefulPriceField;
  let loadoutSelected: undefined | CheckboxField;
  let freightSelected: undefined | CheckboxField;
  let otherChargesSelected: undefined | CheckboxField;
  let carrySelected: undefined | CheckboxField;
  let warehousingSelected: undefined | CheckboxField;
  let loadoutCost: undefined | StatefulPriceField;
  let freightCost: undefined | StatefulPriceField;
  let otherChargesCost: undefined | StatefulPriceField;
  let carryCost: undefined | StatefulPriceField;
  let warehousingCost: undefined | StatefulPriceField;
  let carryPercentagePerMonth: undefined | SelectField;
  let carryMonthsIncluded: undefined | SelectField;

  const fixedPriceTypeField = getField(fields, 'price_type');
  const importMarkupField = getField(fields, 'import_markup');
  const importMarkupUsdLbField = getField(fields, 'import_markup_usd_lb');
  const otherMarkupsField = getField(fields, 'other_markups');
  const otherMarkupsUsdLbField = getField(fields, 'other_markups_usd_lb');
  const fobPriceField = getField(fields, 'fob_price');
  const fobPriceUsdLbField = getField(fields, 'fob_price_usd_lb');
  const spotIcoCostField = getField(fields, 'spot_ico_cost');
  const fixedPriceField = getField(fields, 'fixed_price');
  const priceUsdLbField = getField(fields, 'price_usd_lb');
  const opportunityPriceUsdLbField = getField(fields, 'opportunity_price_usd_lb');
  const opportunityFixedPriceField = getField(fields, 'opportunity_fixed_price');
  const opportunityPricesUsdToCustomerRateField = getField(
    fields,
    'opportunity_prices_usd_to_customer_rate'
  );
  const modifiedExchangeRateField = getField(fields, 'modified_exchange_rate');
  const contractPricesUsdToCustomerRateField = getField(
    fields,
    'contract_prices_usd_to_customer_rate'
  );
  const contractPricesCustomerToUsdRateField = getField(
    fields,
    'contract_prices_customer_to_usd_rate'
  );
  const differentialField = getField(fields, 'differential');
  const opportunityPriceField = getField(fields, 'opportunity_price');
  const internalContractPriceField = getField(fields, 'internal_contract_price');
  const loadoutSelectedField = getField(fields, 'loadout_selected');
  const freightSelectedField = getField(fields, 'freight_selected');
  const otherChargesSelectedField = getField(fields, 'other_charges_selected');
  const carrySelectedField = getField(fields, 'carry_selected');
  const warehousingSelectedField = getField(fields, 'warehousing_selected');
  const carryPercentagePerMonthField = getField(fields, 'carry_percentage_per_month');
  const carryMonthsIncludedField = getField(fields, 'carry_months_included');
  const loadoutCostField = getField(fields, 'loadout_cost');
  const freightCostField = getField(fields, 'freight_cost');
  const otherChargesCostField = getField(fields, 'other_charges_cost');
  const carryCostField = getField(fields, 'carry_cost');
  const warehousingCostField = getField(fields, 'warehousing_cost');
  const unitsField = getField(fields, 'units');
  const packagingTypeField = getField(fields, 'packaging_type_id');

  const otherMarkupsCalculable = () => {
    return (
      loadoutSelected?.value ||
      freightSelected?.value ||
      otherChargesSelected?.value ||
      warehousingSelected?.value
    );
  };

  if (packagingTypeField) {
    selectedPackagingTypeCapacity = createValueSet('0');
  }

  if (unitsField) {
    units = createStatefulPrice(unitsField);
  }

  if (fixedPriceTypeField) {
    fixedPriceType = createPrice(fixedPriceTypeField);
  }

  if (importMarkupField) {
    importMarkup = createStatefulPrice(importMarkupField);
  }

  if (importMarkupUsdLbField) {
    importMarkupUsdLb = createStatefulPrice(importMarkupUsdLbField);
  }

  if (otherMarkupsField) {
    otherMarkups = createStatefulPrice(otherMarkupsField);
  }

  if (otherMarkupsUsdLbField) {
    otherMarkupsUsdLb = createStatefulPrice(otherMarkupsUsdLbField);
  }

  if (fobPriceField) {
    fobPrice = createStatefulPrice(fobPriceField);
  }

  if (fobPriceUsdLbField) {
    fobPriceUsdLb = createStatefulPrice(fobPriceUsdLbField);
  }

  if (spotIcoCostField) {
    spotIcoCost = createStatefulPrice(spotIcoCostField);
  }

  if (fixedPriceField) {
    fixedPrice = createStatefulPrice(fixedPriceField);
  }

  if (differentialField) {
    differentialPrice = createStatefulPrice(differentialField);
  }

  if (opportunityPriceField) {
    opportunityPrice = createStatefulPrice(opportunityPriceField);
  }

  if (internalContractPriceField) {
    internalContractPrice = createStatefulPrice(internalContractPriceField);
  }

  if (loadoutSelectedField) {
    loadoutSelected = createCheckboxPrice(loadoutSelectedField);
  }

  if (freightSelectedField) {
    freightSelected = createCheckboxPrice(freightSelectedField);
  }

  if (otherChargesSelectedField) {
    otherChargesSelected = createCheckboxPrice(otherChargesSelectedField);
  }

  if (carrySelectedField) {
    carrySelected = createCheckboxPrice(carrySelectedField);
  }

  if (warehousingSelectedField) {
    warehousingSelected = createCheckboxPrice(warehousingSelectedField);
  }

  if (loadoutCostField) {
    loadoutCost = createStatefulPrice(loadoutCostField);
  }

  if (freightCostField) {
    freightCost = createStatefulPrice(freightCostField);
  }

  if (otherChargesCostField) {
    otherChargesCost = createStatefulPrice(otherChargesCostField);
  }

  if (carryCostField) {
    carryCost = createStatefulPrice(carryCostField);
  }

  if (warehousingCostField) {
    warehousingCost = createStatefulPrice(warehousingCostField);
  }

  if (carryPercentagePerMonthField) {
    carryPercentagePerMonth = createSelectPrice(carryPercentagePerMonthField);
  }

  if (carryMonthsIncludedField) {
    carryMonthsIncluded = createSelectPrice(carryMonthsIncludedField);
  }

  if (opportunityPriceUsdLbField) {
    opportunityPriceUsdLb = createStatefulPrice(opportunityPriceUsdLbField);
  }

  if (opportunityFixedPriceField) {
    opportunityFixedPrice = createStatefulPrice(opportunityFixedPriceField);
  }

  if (opportunityPricesUsdToCustomerRateField) {
    opportunityPricesUsdToCustomerRate = createStatefulPrice(
      opportunityPricesUsdToCustomerRateField
    );
  }
  if (priceUsdLbField) {
    priceUsdLb = createStatefulPrice(priceUsdLbField);
  }

  if (modifiedExchangeRateField) {
    modifiedExchangeRate = createCheckboxPrice(modifiedExchangeRateField);
  }

  if (contractPricesUsdToCustomerRateField) {
    contractPricesUsdToCustomerRate = createStatefulPrice(
      contractPricesUsdToCustomerRateField
    );
  }

  if (contractPricesCustomerToUsdRateField) {
    contractPricesCustomerToUsdRate = createStatefulPrice(
      contractPricesCustomerToUsdRateField
    );
  }

  const sameCurrency = currency === 'USD';
  const sameUnitofMeasure = unitOfMeasure === 'unit_lb';
  const sameCurrencyUnitofMeasure = sameCurrency && sameUnitofMeasure;

  useEffect(() => {
    if (otherMarkupsCalculable()) {
      const otherMarkupsValue =
        (loadoutSelected?.value && loadoutCost ? parseFloat(loadoutCost.value) : 0) +
        (freightSelected?.value && freightCost ? parseFloat(freightCost.value) : 0) +
        (otherChargesSelected?.value && otherChargesCost
          ? parseFloat(otherChargesCost.value)
          : 0) +
        (warehousingSelected?.value && warehousingCost
          ? parseFloat(warehousingCost.value)
          : 0);

      otherMarkups?.set(otherMarkupsValue.toFixed(2).toString());
    }
  }, [loadoutCost, freightCost, otherChargesCost, warehousingCost]);

  useEffect(() => {
    if (carrySelected?.value) {
      const parsedcarryPercentagePerMonth = parseFloat(
        carryPercentagePerMonth?.value || '0'
      );
      const parsedcarryMonthsIncluded = parseFloat(carryMonthsIncluded?.value || '0');

      carryCost?.set(
        (
          (1 + parsedcarryPercentagePerMonth / 100) ** parsedcarryMonthsIncluded -
          1
        ).toFixed(2)
      );
    }
  }, [carrySelected, carryPercentagePerMonth, carryMonthsIncluded]);

  if (
    fixedPriceType?.value === 'fixed' &&
    !sameCurrencyUnitofMeasure &&
    contractType != 'LocalContract'
  ) {
    useEffect(() => {
      const rate =
        Number(contractPricesCustomerToUsdRate?.value) / Number(measurementRate);
      contractPricesUsdToCustomerRate?.set(
        (1 / Number(contractPricesCustomerToUsdRate?.value)).toFixed(4)
      );
      fobPrice?.set((Number(fobPriceUsdLb?.value) / rate).toFixed(4));
      importMarkup?.set((Number(importMarkupUsdLb?.value) / rate).toFixed(4));
      otherMarkups?.set((Number(otherMarkupsUsdLb?.value) / rate).toFixed(4));
    }, [contractPricesCustomerToUsdRate]);
  }

  useEffect(() => {
    if (contractType === 'InternalContract') {
      fobPrice?.set(fobPrice?.value);
    }
    if (
      fixedPriceType?.value === 'fixed' &&
      sameCurrencyUnitofMeasure &&
      contractType != 'InternalContract' &&
      contractType != 'LocalContract'
    ) {
      fobPrice?.set(Number(fobPriceUsdLb?.value).toFixed(4));
      importMarkup?.set(Number(importMarkupUsdLb?.value).toFixed(4));
      otherMarkups?.set(Number(otherMarkupsUsdLb?.value).toFixed(4));
    }

    if (fixedPriceType?.value === 'differential') {
      fobPrice?.set(Number(fobPriceUsdLb?.value).toFixed(4));
      importMarkup?.set(Number(importMarkupUsdLb?.value).toFixed(4));
      otherMarkups?.set(Number(otherMarkupsUsdLb?.value).toFixed(4));
    }
  }, [fixedPriceType, sameCurrencyUnitofMeasure]);

  useEffect(() => {
    const finalPrice =
      ((importMarkup && parseFloat(importMarkup.value)) || 0) +
      ((otherMarkups && parseFloat(otherMarkups.value)) || 0) +
      ((fobPrice && parseFloat(fobPrice.value)) || 0) +
      ((carryCost && parseFloat(carryCost.value)) || 0);

    const formattedFinalPrice = finalPrice.toFixed(4).toString();

    if (fixedPriceType?.value === 'fixed' && contractType !== 'LocalContract') {
      fixedPrice?.set(formattedFinalPrice);
    } else {
      differentialPrice?.set(formattedFinalPrice);
    }
  }, [importMarkup, otherMarkups, fobPrice, carryCost]);

  useEffect(() => {
    if (contractType !== 'LocalContract') {
      const total =
        Number(fobPriceUsdLb?.value) +
        Number(importMarkupUsdLb?.value || 0) +
        Number(otherMarkupsUsdLb?.value || 0);
      priceUsdLb?.set(total.toFixed(4));
    }
  }, [fobPriceUsdLb, importMarkupUsdLb, otherMarkupsUsdLb]);

  useEffect(() => {
    if (destinationWarehouseId && destinationWarehouseCosts) {
      const destinationWarehouseCost = destinationWarehouseCosts.find(
        (cost) => cost.destinationWarehouseId.toString() === destinationWarehouseId
      );
      if (destinationWarehouseCost) {
        warehousingCost?.set(destinationWarehouseCost.warehousingCost.toString());
      }
    }
  }, [destinationWarehouseId]);

  useEffect(() => {
    const unitValue = parseFloat(units?.value || '0');
    if (destinationWarehouseId && destinationWarehouseCosts && unitValue > 0) {
      const destinationWarehouseCost = destinationWarehouseCosts.find(
        (cost) => cost.destinationWarehouseId.toString() === destinationWarehouseId
      );
      if (destinationWarehouseCost) {
        let loadoutCostValue;
        const perUnitCost = destinationWarehouseCost.loadoutCost.perUnitCost;
        if (destinationWarehouseCost.loadoutType == 'per_unit') {
          loadoutCostValue = perUnitCost * unitValue;
        } else {
          // Using capacity in Lb
          const capacity =
            parseFloat(selectedPackagingTypeCapacity?.value || '0') * 2.2046;
          const weightPerUnitCost =
            destinationWarehouseCost.loadoutCost.weightPerUnitCost;
          loadoutCostValue = ((unitValue * capacity) / weightPerUnitCost) * perUnitCost;
        }
        loadoutCost?.set(loadoutCostValue.toFixed(2).toString());
      }
    }
  }, [destinationWarehouseId, units]);

  return {
    fixedPriceType,
    selectedPackagingTypeCapacity,
    units,
    importMarkup,
    importMarkupUsdLb,
    otherMarkups,
    otherMarkupsUsdLb,
    fobPrice,
    fobPriceUsdLb,
    spotIcoCost,
    fixedPrice,
    priceUsdLb,
    modifiedExchangeRate,
    contractPricesUsdToCustomerRate,
    contractPricesCustomerToUsdRate,
    exchangeRate,
    measurementRate,
    sameCurrency,
    sameUnitofMeasure,
    sameCurrencyUnitofMeasure,
    opportunityPriceUsdLb,
    opportunityFixedPrice,
    opportunityPricesUsdToCustomerRate,
    differentialPrice,
    opportunityPrice,
    internalContractPrice,
    loadoutSelected,
    freightSelected,
    otherChargesSelected,
    carrySelected,
    warehousingSelected,
    incoterm,
    carryPercentagePerMonth,
    carryMonthsIncluded,
    loadoutCost,
    freightCost,
    otherChargesCost,
    carryCost,
    warehousingCost,
    contractType,
    priceBelowSimulator,
    setPriceBelowSimulator,
    fobBelowSimulator,
    setFobBelowSimulator,
  };
};

export const PriceProvider = ({
  value,
  children,
}: {
  value: PriceContext;
  children: React.ReactElement;
}) => {
  return <PriceContext.Provider value={value}>{children}</PriceContext.Provider>;
};
